import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";

import UiButton from "@lovetoknow/ui-button";
import { clamp } from "@lovetoknow/love-utils-js";

import { AD_LAYOUT_STUBS } from "@consts";
import { getRandomWords } from "@/common/services/randomWordService";

import { getCMSCopy } from "@/common/services/cmsCopyService";

dayjs.extend(utc);
dayjs.extend(tz);

const DEFAULT_WORD_COUNT = 5;
const LENGTH_FIELD_VALUES = {
	letters: {
		default: 5,
		min: 2,
		max: 15,
	},
	syllables: {
		default: 3,
		min: 1,
		max: 7,
	},
};

export default {
	name: "RandomWordGenerator",
	data() {
		const lengthMetricOptions = [
			{ label: "No preference", value: null },
			{ label: "Letters", value: "letters" },
			{ label: "Syllables", value: "syllables" },
		];

		const lengthLimitOptions = [
			{ label: "Equals", value: "equals" },
			{ label: "Less than", value: "less" },
			{ label: "More than", value: "more" },
		];

		const posOptions = [
			{ label: "Any", value: null },
			{ label: "Noun", value: "noun" },
			{ label: "Verb", value: "verb" },
			{ label: "Adjective", value: "adjective" },
			{ label: "Adverb", value: "adverb" },
		];

		return {
			AD_LAYOUT_STUBS,
			lengthMetricOptions,
			lengthLimitOptions,
			posOptions,
			isLoading: false,
			searchParams: {
				limit: DEFAULT_WORD_COUNT,
				starts_with: "",
				ends_with: "",
				length: {
					metric: lengthMetricOptions[0].value,
					limit: lengthLimitOptions[0].value,
					value: "",
					isDirty: false,
				},
				pos: posOptions[0].value,
			},
			mobileMoreOptionsShown: true,
			isFirstSearch: true,
		};
	},
	components: {
		UiButton,
	},
	async asyncData({ $axios, $config, res }) {
		let serviceResponse = null;
		let cmsResponse = null;

		// Run this only on the server side.
		if (!process.client) {
			const servicePromise = getRandomWords($axios, $config);
			const cmsPromise = getCMSCopy(
				$axios,
				$config,
				".seo.landing.random-word-generator",
				process.env.version
			);

			// Set cache expiration header:
			if (res) {
				const HOUR = 3;
				const MINUTE = 10;
				const now = dayjs().tz("America/New_York");
				let nextRefreshDate = now
					.hour(HOUR)
					.minute(MINUTE)
					.startOf("minute");
				if (nextRefreshDate.isBefore(now)) {
					// Same time tomorrow
					nextRefreshDate = nextRefreshDate.add(1, "day");
				}
				const expirationDate = nextRefreshDate
					.utc()
					.format("ddd, DD MMM YYYY HH:mm:ss [GMT]");
				res.setHeader("Expires", expirationDate);
			}

			[serviceResponse, cmsResponse] = await Promise.all([
				servicePromise,
				cmsPromise,
			]);
		}

		return {
			/**
			 * @note
			 * This flag is to inform the page component whether this was a server load or a client one. So that even though the mounted hook runs in both scenarios, we know to only query the API in the mounted hook in the latter scenario.
			 */
			isClientMode: process.client,
			cmsContent: cmsResponse?.data || {},
			words: serviceResponse?.data || [],
		};
	},
	head() {
		return {
			title: this.cmsContent?.metaTitle,
			meta: [
				{
					name: "description",
					hid: "description",
					content: this.cmsContent?.metaDescription,
				},
			],
		};
	},
	computed: {
		bodyCopy() {
			return this.cmsContent?.body || "";
		},
		titleText() {
			return this.cmsContent?.title;
		},
		subtitleText() {
			return this.cmsContent?.teaser;
		},
		generateButtonText() {
			return this.isLoading ? "Generating" : "Generate Words";
		},
		wordLengthValues() {
			if (!LENGTH_FIELD_VALUES[this.searchParams.length.metric]) {
				return {
					...LENGTH_FIELD_VALUES.letters,
					default: null,
				};
			}

			const values = LENGTH_FIELD_VALUES[this.searchParams.length.metric];

			let min = values.min;

			if (
				this.searchParams.length.limit ===
				this.lengthLimitOptions[1].value
			) {
				min++;
			}

			let max = values.max;

			if (
				this.searchParams.length.limit ===
				this.lengthLimitOptions[2].value
			) {
				max--;
			}

			return {
				default: values.default,
				min,
				max,
			};
		},
		_wordCount: {
			get() {
				return this.searchParams.limit;
			},
			set(val) {
				// We sanitize the value only if there is a value
				// Also, for comparison only we convert the value to string,
				// so we wont have problems with the fact that a Boolean(0) is false...
				if (val.toString()) {
					val = typeof val === "number" ? val : DEFAULT_WORD_COUNT;
					val = clamp(val, 1, 50);
				}
				this.searchParams.limit = val;
				this.$refs.wordCount.value = val;
			},
		},
		_starts: {
			get() {
				return this.searchParams.starts_with || "";
			},
			set(val) {
				val = this.cleanInputValue(val);
				this.searchParams.starts_with = val;
				this.$refs.startsField.value = val;
			},
		},
		_ends: {
			get() {
				return this.searchParams.ends_with || "";
			},
			set(val) {
				val = this.cleanInputValue(val);
				this.searchParams.ends_with = val;
				this.$refs.endsField.value = val;
			},
		},
		_lengthValue: {
			get() {
				return parseInt(this.searchParams.length.value) || "";
			},
			set(val) {
				if (val !== "") {
					const limitValues = this.wordLengthValues;
					if (isNaN(parseInt(val))) {
						val = limitValues.default;
					}

					val = parseInt(val);
					val = clamp(val, limitValues.min, limitValues.max);
				}

				if (this.searchParams.length.value !== val) {
					this.searchParams.length.value = val;
					this.searchParams.length.isDirty = true;
				}
			},
		},
		noLengthMetricSelected() {
			return this.searchParams.length.metric === null;
		},
		numberOfOptionsSelected() {
			let options = 0;
			if (this._starts) {
				options++;
			}
			if (this._ends) {
				options++;
			}
			if (this._lengthValue) {
				options++;
			}

			if (this.searchParams?.pos) {
				options++;
			}

			return options;
		},
		numberOfOptionsSelectedLabel() {
			return `${this.numberOfOptionsSelected} selected`;
		},
		showMoreOptionsButtonText() {
			return this.mobileMoreOptionsShown
				? "Fewer options"
				: "More options";
		},
		showMoreOptions() {
			if (this.isSmallDevice && !this.mobileMoreOptionsShown) {
				return false;
			}

			return true;
		},
	},
	methods: {
		cleanInputValue(val) {
			val = typeof val === "string" ? val : "";
			val = val.replace(/[^a-z]/gi, "");
			val = val.substring(0, 5);
			return val;
		},
		onDecreaseWordCountClick() {
			this._wordCount -= 1;
		},
		onIncreaseWordCountClick() {
			this._wordCount += 1;
		},
		onLengthMetricRadioInputInput(val) {
			// If this is not one of the allowed values, default to the first valid value.
			if (!this.lengthMetricOptions.map((o) => o.value).includes(val)) {
				val = this.lengthMetricOptions[0].value;
			}

			this.searchParams.length.metric = val;

			if (
				(!this.searchParams.length.isDirty || !this._lengthValue) &&
				val !== this.lengthMetricOptions[0].value
			) {
				this.searchParams.length.value = this.wordLengthValues.default;
			} else if (val === this.lengthMetricOptions[0].value) {
				this.searchParams.length.value = "";
			}

			this.clampLengthValue();
		},
		onLengthLimitChange(val) {
			// Make sure the value is a valid one.
			if (!this.lengthLimitOptions.map((o) => o.value).includes(val)) {
				val = this.lengthLimitOptions[0].value;
			}

			this.searchParams.length.limit = val;
			this.clampLengthValue();
		},
		onFormSubmit() {
			this.loadWords();
			this.mobileMoreOptionsShown = false;
			this.isFirstSearch = false;
		},
		clampLengthValue() {
			if (!this._lengthValue) {
				return;
			}
			const limitValues = this.wordLengthValues;
			this.searchParams.length.value = clamp(
				this._lengthValue,
				limitValues.min,
				limitValues.max
			);
		},
		getSearchParams() {
			const s = this.searchParams;

			const params = {
				limit: s.limit,
				starts_with: s.starts_with,
				ends_with: s.ends_with,
			};

			if (s.length.metric !== null && s.length.value) {
				const lengthKey = s.length.metric;

				const minKey = `${lengthKey}_min`;
				const maxKey = `${lengthKey}_max`;

				const value = s.length.value;

				switch (s.length.limit) {
					case this.lengthLimitOptions[0].value:
						params[minKey] = value;
						params[maxKey] = value;
						break;
					case this.lengthLimitOptions[1].value:
						params[maxKey] = parseInt(value) - 1;
						break;
					case this.lengthLimitOptions[2].value:
						params[minKey] = parseInt(value) + 1;
						break;
				}
			}

			if (s.pos !== null) {
				params.pos = s.pos;
			}

			return params;
		},
		loadWords() {
			this.isLoading = true;

			getRandomWords(this.$axios, this.$config, this.getSearchParams())
				.then((response) => {
					this.words = response?.data || [];
				})
				.finally(() => {
					this.isLoading = false;
				});
		},
		onWordCountInputChanged(e) {
			if (e?.target?.value) {
				return;
			}

			// If there is no value, we set it to the default one
			this._wordCount = DEFAULT_WORD_COUNT;
		},
		onMoreOptionsButtonClick() {
			this.mobileMoreOptionsShown = !this.mobileMoreOptionsShown;
		},
		onResetButtonClick() {
			this.searchParams.limit = DEFAULT_WORD_COUNT;
			this.searchParams.starts_with = "";
			this.searchParams.ends_with = "";
			this.searchParams.length = {
				metric: this.lengthMetricOptions[0].value,
				limit: this.lengthLimitOptions[0].value,
				value: "",
				isDirty: false,
			};
			this.searchParams.pos = this.posOptions[0].value;
		},
	},
	mounted() {
		if (this.isClientMode) {
			this.loadWords();

			getCMSCopy(
				this.$axios,
				this.$config,
				".seo.landing.random-word-generator",
				process.env.version
			).then((cmsResponse) => {
				this.cmsContent = cmsResponse?.data || {};
			});
		}
	},
};
